import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { ConopsIconAttributeMap } from "../../../esri/graphicLayerUtils/Conops";
import { setConopsActivity } from "../../../redux/action/Conops-actions";
import { ConfigContext } from "../../../utils/ConfigContext";
import { getActivities } from "../../../utils/LC_API";
import { FiSend } from "../../Icons";
import { iconNameToIcon, svgToBase } from "../../Icons/layerSvg/clusterAssets";
import { Button, IconButton, Tooltip } from "../../UI";
import Loader from "../Editor/Loader/Loader";
import {
  StyledConopsContainer,
  StyledConopsItemContainer,
  StyledDescriptionContainer,
  StyledFooterContainer,
  StyledIconContainer,
  StyledSidebarContainer,
  StyledSidebarTitle,
  StyledTitleContainer,
} from "./ConopsInformation-styled";

const ConopsFieldMap = {
  coordination: "hascoordination",
  informationmanagement: "hasinfomgnt",
  storage: "hasstorage",
  overlandtransport: "hasroadtransport",
  airtransport: "hasairfixed",
  rivertransport: "hasrivertransport",
  seaandriverstransport: "hasseatransport",
  preparedness: "haspreparedness",
  training: "hastraining",
};

function SvgIcon({ iconName, width = 28, height = 28 }) {
  const ref = useRef(null);
  const svg = svgToBase(iconName);

  // Replace fill color in SVG string
  // const coloredSvg = svgString.replace(/fill="[^"]*"/g, `fill="${color}"`);
  return (
    <StyledIconContainer width={width} height={height} ref={ref}>
      <img src={svg} />
    </StyledIconContainer>
  );
  // return (
  //   <StyledIconContainer
  //     width={width}
  //     height={height}
  //     ref={ref}
  //     dangerouslySetInnerHTML={{ __html: coloredSvg }}
  //   />
  // );
}

const ConopsInformation = ({ expand, processTab, isSelected }) => {
  const { t } = useTranslation("common");
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState([]);
  const activity = useSelector((state) => state.conopsReducer.activity);
  const dispatch = useDispatch();

  const { config, activeLanguage } = useContext(ConfigContext);

  const activityId = useMemo(() => config?.activityId ?? 0, [config]);

  const fetchActivitiesWithFallback = async (lang, fallbackLang = "en") => {
    try {
      const response = await getActivities(lang);
      if (response.ok) {
        return { data: await response.json(), usedLang: lang };
      }
      throw new Error("Failed to fetch activities");
    } catch (error) {
      console.log(error, lang, fallbackLang);

      if (lang !== fallbackLang) {
        try {
          const fallbackResponse = await getActivities(fallbackLang);
          if (fallbackResponse.ok) {
            return {
              data: await fallbackResponse.json(),
              usedLang: fallbackLang,
            };
          }
        } catch (fallbackError) {
          throw fallbackError;
        }
      }
      throw error;
    }
  };

  useEffect(() => {
    if (activity && activity[activeLanguage]) {
      return;
    }
    setLoading(true);
    fetchActivitiesWithFallback(activeLanguage)
      .then(({ data, usedLang }) => {
        const { rows } = data;
        rows.forEach((activityItem) => {
          if (activityItem.id === String(activityId)) {
            dispatch(
              setConopsActivity({
                [usedLang]: activityItem,
              })
            );
          }
        });
      })
      .catch((error) => {
        console.error("Error fetching activities:", error);
      })
      .finally(() => {
        setLoading(false);
      });
    return () => {};
  }, [activityId, activeLanguage]);

  const handleConopsSelection = useCallback(
    (title) => {
      if (selected.includes(title)) {
        setSelected((prev) => prev.filter((item) => item !== title));
      } else {
        setSelected((prev) => prev.concat(title));
      }
    },
    [selected]
  );

  const conopsData = useMemo(() => {
    if (!activity) return [];
    const currentLanguageActivity = activity[activeLanguage] ?? activity["en"];

    if (!currentLanguageActivity || !currentLanguageActivity.conops) return [];
    try {
      const formattedConops = currentLanguageActivity.conops.map((item) => {
        const [title, description] = Object.entries(item)[0];
        const normalize = (str) => str.replace(/\s+/g, "").toLowerCase();
        const field = ConopsFieldMap[normalize(title)] || "hascoordination";
        const iconMap = ConopsIconAttributeMap[field];
        const iconName = iconMap?.iconName;

        return { title, description, iconName };
      });

      return formattedConops;
    } catch (error) {
      console.error("Error formatting conops data:", error);
      return [];
    }
  }, [activity, activeLanguage]);

  useEffect(() => {
    processTab({ tab: "conops", visible: conopsData.length > 0 });
  }, [conopsData]);

  const currentAcvitity = useMemo(() => {
    return activity[activeLanguage] ?? activity["en"];
  }, [activity, activeLanguage]);

  if (!isSelected) return null;

  if (loading) {
    return <Loader bg="transparent" position="absolute" />;
  }

  return (
    <StyledSidebarContainer expand={expand}>
      <StyledSidebarTitle>{t("screen.widget.Conops.title")}</StyledSidebarTitle>
      <StyledConopsContainer>
        {conopsData.map((item) => (
          <StyledConopsItemContainer key={item.title}>
            <StyledTitleContainer
              onClick={() => handleConopsSelection(item.title)}
            >
              {item.iconName ? (
                <SvgIcon
                  svgString={iconNameToIcon(item.iconName)}
                  iconName={item.iconName}
                  width={28}
                  height={28}
                  color="#fff"
                />
              ) : (
                <IconButton
                  bg={config.opsColor}
                  style={{
                    borderRadius: "100px",
                    width: "28px",
                    height: "28px",
                    minWidth: 28,
                    minHeight: 28,
                  }}
                ></IconButton>
              )}

              {item.title && <span>{item.title}</span>}
            </StyledTitleContainer>
            <StyledDescriptionContainer selected={true}>
              {item.description && <span>{item.description}</span>}
            </StyledDescriptionContainer>
          </StyledConopsItemContainer>
        ))}
      </StyledConopsContainer>
      {currentAcvitity && (
        <StyledFooterContainer>
          <Button
            isNew
            onClick={() => window.open(currentAcvitity.path, "_blank")}
            style={{ position: "relative", fontSize: "12px" }}
          >
            <Tooltip
              placement="top"
              showOnHover
              content={t("screen.widget.Conops.buttons.page.information")}
              wrapperStyles={{
                width: "100%",
                height: "100%",
                position: "absolute",
              }}
              blurred
            />
            <svg
              id="logisticsClusterSmallIcon"
              xmlns="http://www.w3.org/2000/svg"
              version="1.2"
              viewBox="0 0 350 420"
              height="16px"
              fill="currentColor"
              width="16px"
            >
              <path className="a" d="m225 78l-89.5 51.9-135-77.9 89.4-52z" />
              <path
                className="a"
                d="m94.9 315.5l174.7-101.6 90 52-264.7 153.9z"
              />
              <path className="a" d="m0.5 367.8v-310.4l90 52v310.4z" />
            </svg>
            <span>{t("screen.widget.Conops.buttons.page.title")}</span>
          </Button>
          {currentAcvitity.rita_id && (
            <Button
              isNew
              onClick={() =>
                window.open(
                  `https://rita.logcluster.org/public/onlinesrf.htm?lang=${activeLanguage}&prId=${currentAcvitity.rita_id}`,
                  "_blank"
                )
              }
              style={{ position: "relative", fontSize: "12px" }}
            >
              <Tooltip
                placement="top"
                showOnHover
                content={t("screen.widget.Conops.buttons.ritaForm.information")}
                wrapperStyles={{
                  width: "100%",
                  height: "100%",
                  position: "absolute",
                }}
                blurred
              />
              <FiSend width="16" height="16" />
              <span>{t("screen.widget.Conops.buttons.ritaForm.title")}</span>
            </Button>
          )}
        </StyledFooterContainer>
      )}
    </StyledSidebarContainer>
  );
};

export default ConopsInformation;
